import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap, finalize } from 'rxjs/operators';
import {
    ConfigureWhiteLabelGQL,
    WhiteLabel,
    ConfigureWhiteLabelCommand,
    WhiteLabelGQL,
    CheckSubdomainAvailabilityGQL,
    WhiteLabelLogoSignUrlGQL, WhiteLabelLogoSignUrlRequest,
    WhiteLabelBySubdomainGQL
} from '../../../../graphql'

export interface WhiteLabelTheme {
    primary1: string;
    primary2: string;
    primary3: string;
    primary4: string;
    headerColor: string;
    primaryGreen: string;
    fontFamily: {
        heading: string;
        body: string;
    };
}

export interface LogoConfig {
    rectangle: string;
    square: string;
    fav: string;
}

interface BlobUploadUrlSigner {
    contentType: string;
    httpMethod: string;
    signedUrl: string;
    url: string;
}

@Injectable({
    providedIn: 'root'
})
export class WhiteLabelService {
    // Default brand colors
    static readonly DEFAULT_COLORS = {
        PRIMARY_COLOR: '#1E3D59',
        SECONDARY_COLOR: '#FFC13B',
        TERTIARY_COLOR: '#F5F5F5',
        QUATERNARY_COLOR: '#2C3E50',
        HEADER_COLOR: '#F8F9FA',
        PRIMARY_GREEN: '#7C9D96'
    };

    private readonly isShowcaseDomain: boolean;
    private readonly subdomain: string | null;
    private _loading = new BehaviorSubject<boolean>(false);
    private _subdomainCheckLoading = new BehaviorSubject<boolean>(false);
    private _whiteLabelConfig = new BehaviorSubject<WhiteLabel | null>(null);
    
    // Public observables
    readonly loading$ = this._loading.asObservable();
    readonly subdomainCheckLoading$ = this._subdomainCheckLoading.asObservable();
    readonly whiteLabelConfig$ = this._whiteLabelConfig.asObservable();
    readonly hasConfig$ = this.whiteLabelConfig$.pipe(
        map(config => config !== null)
    );

    constructor(
        private _whiteLabelGQL: WhiteLabelGQL,
        private _configureWhiteLabelGQL: ConfigureWhiteLabelGQL,
        private _checkSubdomainAvailabilityGQL: CheckSubdomainAvailabilityGQL,
        private _whiteLabelLogoSignUrlGQL: WhiteLabelLogoSignUrlGQL,
        private _whiteLabelBySubdomainGQL: WhiteLabelBySubdomainGQL,
    ) {
        const currentDomain = window.location.host;
        this.isShowcaseDomain = currentDomain.includes('showcase.');
        
        if (this.isShowcaseDomain) {
            // Extract white label name from subdomain (e.g., flok-pro.showcase.reservests.com -> flok-pro)
            this.subdomain = currentDomain.split('.')[0];
            console.log('White Label Service: Detected showcase domain', {
                domain: currentDomain,
                whiteLabel: this.subdomain
            });
        } else {
            this.subdomain = null;
            console.log('White Label Service: Not a showcase domain', { domain: currentDomain });
        }
    }

    // Get current config synchronously
    getCurrentConfig(): WhiteLabel | null {
        return this._whiteLabelConfig.getValue();
    }

    // Check if config is loaded
    isLoaded(): boolean {
        return this._whiteLabelConfig.getValue() !== null;
    }

    getLogos(): LogoConfig | null {
        const config = this.getCurrentConfig();
        if (!config) {
            return null;
        }
        
        return {
            rectangle: config.landscapeLogoUrl || '',
            square: config.squareLogoUrl || '',
            fav: config.faviconUrl || ''
        };
    }

    getTheme(): WhiteLabelTheme | null {
        const config = this.getCurrentConfig();
        if (!config) {
            return null;
        }
        
        return {
            primary1: config.primaryColor || WhiteLabelService.DEFAULT_COLORS.PRIMARY_COLOR,
            primary2: config.secondaryColor || WhiteLabelService.DEFAULT_COLORS.SECONDARY_COLOR,
            primary3: config.tertiaryColor || WhiteLabelService.DEFAULT_COLORS.TERTIARY_COLOR,
            primary4: config.quaternaryColor || WhiteLabelService.DEFAULT_COLORS.QUATERNARY_COLOR,
            headerColor: config.headerColor || WhiteLabelService.DEFAULT_COLORS.HEADER_COLOR,
            primaryGreen: config.primaryGreen || WhiteLabelService.DEFAULT_COLORS.PRIMARY_GREEN,
            fontFamily: {
                heading: config.headingFont || 'the-seasons, serif',
                body: config.bodyFont || 'Poppins, sans-serif'
            }
        };
    }

    loadWhiteLabel(resellerId: string): Observable<WhiteLabel | null> {
        console.log('Loading white label config for resellerId:', resellerId);
        this._loading.next(true);
        
        return this._whiteLabelGQL
            .watch({ resellerId }, { fetchPolicy: 'no-cache' })
            .valueChanges
            .pipe(
                tap({
                    next: (result) => {
                        console.log('White label GraphQL result:', result);
                        if (result?.errors?.length) {
                            console.error('Error loading white label config:', result.errors);
                            this._whiteLabelConfig.next(null);
                        } else if (!result.loading) {
                            const config = result.data?.whiteLabel;
                            console.log('Setting white label config:', config);
                            this._whiteLabelConfig.next(config as WhiteLabel);
                        }
                        this._loading.next(false);
                    },
                    error: (error) => {
                        console.error('Error loading white label config:', error);
                        this._whiteLabelConfig.next(null);
                        this._loading.next(false);
                    }
                }),
                map(result => result.data?.whiteLabel as WhiteLabel | null)
            );
    }

    loadWhiteLabelBySubdomain(): Observable<WhiteLabel | null> {
        console.log('Loading white label config for subdomain:', this.subdomain);
        this._loading.next(true);
        
        return this._whiteLabelBySubdomainGQL
            .watch({ subdomain: this.subdomain }, { fetchPolicy: 'no-cache' })
            .valueChanges
            .pipe(
                tap({
                    next: (result) => {
                        console.log('White label GraphQL result:', result);
                        if (result?.errors?.length) {
                            console.error('Error loading white label config:', result.errors);
                            this._whiteLabelConfig.next(null);
                        } else if (!result.loading) {
                            const config = result.data?.whiteLabelBySubdomain;
                            console.log('Setting white label config:', config);
                            this._whiteLabelConfig.next(config as WhiteLabel);
                        }
                        this._loading.next(false);
                    },
                    error: (error) => {
                        console.error('Error loading white label config:', error);
                        this._whiteLabelConfig.next(null);
                        this._loading.next(false);
                    }
                }),
                map(result => result.data?.whiteLabelBySubdomain as WhiteLabel | null)
            );
    }

    isWhiteLabelEnabled(): boolean {
        return this.isShowcaseDomain && !!this.subdomain;
    }

    configureWhiteLabel(input: ConfigureWhiteLabelCommand): Observable<boolean> {
        this._loading.next(true);
        return this._configureWhiteLabelGQL
            .mutate({ input })
            .pipe(
                tap({
                    next: (result) => {
                        if (result.data?.configureWhiteLabel?.id) {
                            this.loadWhiteLabel(input.resellerId);
                        }
                    },
                    error: (error) => {
                        console.error('Error configuring white label:', error);
                        this._whiteLabelConfig.next(null);
                    }
                }),
                map(result => !!result.data?.configureWhiteLabel?.id),
                finalize(() => {
                    this._loading.next(false);
                })
            );
    }

    checkSubdomainExists(subdomain: string): Observable<boolean> {
        this._subdomainCheckLoading.next(true);
        return this._checkSubdomainAvailabilityGQL
            .fetch({ subdomain }, { fetchPolicy: 'network-only' })
            .pipe(
                map(result => result.data?.checkSubdomainAvailability?.available ?? false),
                finalize(() => this._subdomainCheckLoading.next(false))
            );
    }

    getSignedUrl(input: { resellerId: string; filename: string }): Observable<BlobUploadUrlSigner> {
        return this._whiteLabelLogoSignUrlGQL
            .mutate({
                input: {
                    resellerId: input.resellerId,
                    filename: input.filename
                } as WhiteLabelLogoSignUrlRequest
            })
            .pipe(
                map(result => result.data?.whiteLabelLogoSignUrl as BlobUploadUrlSigner)
            );
    }
}
